import { useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCalculateEVMutation } from './useCalculateEV';
import { CalculateEVRequest, CalculateEVResponse } from '../data/evTypes';
import { setPlayerCustomEVData, clearPlayerCustomEVData } from '../store/slice';
import { IRootState } from '../../../store/reducer';

interface UseDebouncedEVCalculationOptions {
  debounceMs?: number;
  enabled?: boolean;
}

interface UseDebouncedEVCalculationReturn {
  triggerCalculation: (request: CalculateEVRequest) => void;
  cancel: () => void;
  data: CalculateEVResponse | null;
  isLoading: boolean;
  error: Error | null;
  isCustom: boolean;
}

/**
 * Hook for debounced EV calculation with race condition prevention.
 *
 * Features:
 * - Debounces API calls to prevent excessive requests during rapid input
 * - Tracks request sequence IDs to prevent stale responses from overwriting fresh data
 * - Resets state when context (playerId, gameId) changes
 * - Provides loading, error, and custom states for UI feedback
 */
export function useDebouncedEVCalculation(
  playerId: string,
  gameId: string,
  options: UseDebouncedEVCalculationOptions = {}
): UseDebouncedEVCalculationReturn {
  const { debounceMs = 500, enabled = true } = options;
  const dispatch = useDispatch();
  const mutation = useCalculateEVMutation();

  // Explicit state tracking for "has custom calculation"
  const [hasCustomCalculation, setHasCustomCalculation] = useState(false);
  const [customData, setCustomData] = useState<CalculateEVResponse | null>(null);

  // Race condition prevention
  const requestSequenceRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const contextRef = useRef({ playerId, gameId });

  // Track context changes - reset custom state when context changes
  useEffect(() => {
    const contextChanged =
      contextRef.current.playerId !== playerId ||
      contextRef.current.gameId !== gameId;

    if (contextChanged) {
      // Clear Redux state for old player
      dispatch(clearPlayerCustomEVData(contextRef.current.playerId));
      // Invalidate pending requests
      requestSequenceRef.current++;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      mutation.reset();
      setHasCustomCalculation(false);
      setCustomData(null);
      contextRef.current = { playerId, gameId };
    }
  }, [playerId, gameId, mutation, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      requestSequenceRef.current++;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Watch Redux state - if cleared externally (e.g., by MatchupHeader reset), sync local state
  const reduxCustomEVData = useSelector(
    (state: IRootState) => state.stokasticProps.playerCustomEVData[playerId]
  );

  // Track previous Redux state to detect external clears (transition from data to undefined)
  const prevReduxDataRef = useRef<CalculateEVResponse | undefined>(reduxCustomEVData);

  useEffect(() => {
    const hadData = prevReduxDataRef.current !== undefined;
    const hasData = reduxCustomEVData !== undefined;

    // Update ref for next comparison
    prevReduxDataRef.current = reduxCustomEVData;

    // Only reset if Redux was externally cleared (had data -> no data) while we have local state
    // This prevents resetting during normal calculation flow (where Redux starts as undefined)
    if (hadData && !hasData && hasCustomCalculation) {
      setHasCustomCalculation(false);
      setCustomData(null);
      requestSequenceRef.current++; // Invalidate any pending requests
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      mutation.reset();
    }
  }, [reduxCustomEVData, hasCustomCalculation, mutation]);

  // Debounced calculation trigger with race condition handling
  const triggerCalculation = useCallback((request: CalculateEVRequest) => {
    if (!enabled) {
      return;
    }

    // Clear any pending debounced call
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const thisRequestId = ++requestSequenceRef.current;
    const contextAtStart = { ...contextRef.current };

    timeoutRef.current = setTimeout(() => {
      // Verify context hasn't changed before making request
      if (
        requestSequenceRef.current !== thisRequestId ||
        contextRef.current.playerId !== contextAtStart.playerId ||
        contextRef.current.gameId !== contextAtStart.gameId
      ) {
        return; // Context changed, skip request
      }

      // Fire mutation - use .then/.catch to avoid setTimeout async/throw issues
      mutation.mutateAsync(request)
        .then((result) => {
          // Only update state if still current request
          if (requestSequenceRef.current === thisRequestId && result.success && result.data) {
            setCustomData(result.data);
            setHasCustomCalculation(true);
            // Also dispatch to Redux for TopBetsTable to use
            dispatch(setPlayerCustomEVData({ playerId: contextAtStart.playerId, data: result.data }));
          }
        })
        .catch(() => {
          // Error is captured in mutation state
        });
    }, debounceMs);
  }, [mutation, debounceMs, enabled, dispatch]);

  // Cancel pending requests and reset state
  const cancel = useCallback(() => {
    requestSequenceRef.current++;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    mutation.reset();
    setHasCustomCalculation(false);
    setCustomData(null);
    // Clear Redux state
    dispatch(clearPlayerCustomEVData(contextRef.current.playerId));
  }, [mutation, dispatch]);

  return {
    triggerCalculation,
    cancel,
    data: customData,
    isLoading: mutation.isPending,
    error: mutation.error,
    isCustom: hasCustomCalculation,
  };
}
